import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, PieChart, LineChart, TrendingUp, ArrowRight, CheckCircle, 
  Play, Star, Download, Filter, Calendar, Eye, Settings, Users,
  FileText, Share, Clock, Target, ChevronRight, Code, Zap
} from 'lucide-react';

const ReportingAnalytics = () => {
  const [activeTab, setActiveTab] = useState('reports');

  const reportingFeatures = [
    {
      id: 'reports',
      name: 'Custom Report Builder',
      icon: FileText,
      description: 'Drag-and-drop report creation with advanced filtering and grouping',
      capabilities: [
        'Visual report designer with intuitive interface',
        'Advanced filtering with multiple conditions and logic',
        'Grouping, summarization, and aggregation functions',
        'Custom parameters and dynamic variables',
        'Scheduled report generation and delivery',
        'Multiple output formats (PDF, Excel, CSV)'
      ],
      apiEndpoints: [
        { name: 'getAllReports', description: 'List all available reports' },
        { name: 'saveReport', description: 'Create or update reports' },
        { name: 'executeReport', description: 'Run reports and get results' },
        { name: 'getReportDetails', description: 'Get report configuration' },
        { name: 'downloadReportData', description: 'Export report data' },
        { name: 'deleteReport', description: 'Remove reports safely' }
      ],
      examples: [
        'Sales performance reports by region and time period',
        'Financial statements with drill-down capabilities',
        'Operational KPI reports with trend analysis',
        'Customer segmentation and behavior reports'
      ]
    },
    {
      id: 'dashboards',
      name: 'Interactive Dashboards',
      icon: BarChart3,
      description: 'Real-time dashboards with customizable widgets and charts',
      capabilities: [
        'Drag-and-drop dashboard builder interface',
        'Real-time data updates and refresh capabilities',
        'Interactive charts with drill-down functionality',
        'Mobile-responsive design for all devices',
        'Custom widgets and visualization components',
        'Dashboard sharing and collaboration features'
      ],
      apiEndpoints: [
        { name: 'getAllDashboards', description: 'List all dashboards' },
        { name: 'saveDashboard', description: 'Create or update dashboards' },
        { name: 'deleteDashboard', description: 'Remove dashboards' }
      ],
      examples: [
        'Executive dashboards for C-suite decision making',
        'Operational monitoring centers with real-time KPIs',
        'Customer service performance tracking dashboards',
        'Sales pipeline and forecasting dashboards'
      ]
    },
    {
      id: 'analytics',
      name: 'Advanced Analytics',
      icon: TrendingUp,
      description: 'Powerful analytics engine with predictive capabilities',
      capabilities: [
        'Statistical analysis and data modeling',
        'Trend identification and forecasting',
        'Comparative analysis across time periods',
        'Cohort analysis and customer segmentation',
        'Performance benchmarking and KPI tracking',
        'Automated insight generation and alerts'
      ],
      apiEndpoints: [
        { name: 'executeReport', description: 'Run complex analytical queries' },
        { name: 'getInsights', description: 'Get AI-powered insights' }
      ],
      examples: [
        'Customer lifetime value analysis and prediction',
        'Market trend analysis and opportunity identification',
        'Operational efficiency optimization recommendations',
        'Risk assessment and mitigation strategies'
      ]
    }
  ];

  const chartTypes = [
    { name: 'Bar Charts', icon: '📊', description: 'Compare categories and values' },
    { name: 'Line Charts', icon: '📈', description: 'Show trends over time' },
    { name: 'Pie Charts', icon: '🥧', description: 'Display proportions and percentages' },
    { name: 'Scatter Plots', icon: '🔵', description: 'Analyze correlations and relationships' },
    { name: 'Heat Maps', icon: '🔥', description: 'Visualize data density and patterns' },
    { name: 'Gauges', icon: '⏱️', description: 'Display KPIs and performance metrics' },
    { name: 'Tables', icon: '📋', description: 'Present detailed data in tabular format' },
    { name: 'Maps', icon: '🗺️', description: 'Geographic data visualization' }
  ];

  const reportTypes = [
    {
      category: 'Financial Reports',
      icon: '💰',
      reports: ['P&L Statements', 'Balance Sheets', 'Cash Flow', 'Budget vs Actual']
    },
    {
      category: 'Sales Reports',
      icon: '📈',
      reports: ['Sales Performance', 'Pipeline Analysis', 'Territory Reports', 'Commission Tracking']
    },
    {
      category: 'Operational Reports',
      icon: '⚙️',
      reports: ['Productivity Metrics', 'Quality Control', 'Inventory Status', 'Resource Utilization']
    },
    {
      category: 'HR Reports',
      icon: '👥',
      reports: ['Employee Performance', 'Attendance Tracking', 'Training Records', 'Compensation Analysis']
    }
  ];

  const analyticsCapabilities = [
    {
      name: 'Predictive Analytics',
      icon: Target,
      description: 'Forecast future trends and outcomes',
      features: ['Machine learning models', 'Trend forecasting', 'Risk prediction', 'Demand planning']
    },
    {
      name: 'Real-time Monitoring',
      icon: Clock,
      description: 'Live data tracking and alerts',
      features: ['Real-time dashboards', 'Automated alerts', 'Threshold monitoring', 'Event tracking']
    },
    {
      name: 'Comparative Analysis',
      icon: BarChart3,
      description: 'Compare performance across dimensions',
      features: ['Period comparisons', 'Benchmark analysis', 'Variance reporting', 'Trend analysis']
    },
    {
      name: 'Data Exploration',
      icon: Eye,
      description: 'Interactive data discovery tools',
      features: ['Drill-down capabilities', 'Filter interactions', 'Data pivoting', 'Ad-hoc queries']
    }
  ];

  const testimonials = [
    {
      quote: "The reporting capabilities transformed our monthly board meetings. We now have real-time insights instead of outdated spreadsheets.",
      name: "",
      title: "",
      company: "TechStart Inc",
      avatar: "👩‍💼",
      metric: "75% faster reporting"
    },
    {
      quote: "Interactive dashboards gave our sales team visibility they never had before. Our conversion rates improved by 40%.",
      name: "",
      title: "",
      company: "Growth Dynamics",
      avatar: "👨‍💼",
      metric: "40% higher conversions"
    },
    {
      quote: "The predictive analytics helped us identify market opportunities 6 months ahead of our competitors.",
      name: "",
      title: "",
      company: "Market Leaders",
      avatar: "👩‍🔬",
      metric: "6 months competitive advantage"
    }
  ];

  const stats = [
    { number: '1000+', label: 'Reports Generated Daily', description: 'Across all client organizations' },
    { number: '99.9%', label: 'Dashboard Uptime', description: 'Real-time data availability' },
    { number: '<2s', label: 'Report Load Time', description: 'Average response time' },
    { number: '50+', label: 'Chart Types', description: 'Visualization options available' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white mb-6">
              <BarChart3 className="h-10 w-10" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Reporting & Analytics
              <span className="block text-3xl lg:text-4xl font-normal mt-2 text-green-200">
                Insights That Drive Decisions
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 mb-10 leading-relaxed">
              Transform raw data into actionable insights with powerful reporting tools, 
              interactive dashboards, and advanced analytics capabilities.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Try Reporting Free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="https://www.youtube.com/@nocodeAquargin"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Analytics Demo
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="text-4xl font-bold text-green-600 mb-2">{stat.number}</div>
                <div className="text-gray-900 font-semibold mb-1">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Reporting & Analytics Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive tools for data visualization, analysis, and insights
            </p>
          </div>

          {/* Feature Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {reportingFeatures.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setActiveTab(feature.id)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeTab === feature.id
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <feature.icon className="h-5 w-5" />
                <span>{feature.name}</span>
              </button>
            ))}
          </div>

          {/* Active Feature Content */}
          {reportingFeatures.map((feature) => (
            activeTab === feature.id && (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-6xl mx-auto"
              >
                <div className="grid lg:grid-cols-2 gap-12">
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-4">
                        <feature.icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">{feature.name}</h3>
                    </div>
                    <p className="text-xl text-gray-600 mb-8">{feature.description}</p>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        Key Capabilities
                      </h4>
                      <ul className="space-y-3">
                        {feature.capabilities.map((capability, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{capability}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="bg-gray-50 p-6 rounded-2xl">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Code className="h-5 w-5 text-green-500 mr-2" />
                        API Endpoints
                      </h4>
                      <div className="space-y-3">
                        {feature.apiEndpoints.map((endpoint, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg border-l-4 border-green-500">
                            <div className="font-mono text-sm text-green-600 font-semibold">
                              {endpoint.name}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {endpoint.description}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-green-50 p-6 rounded-2xl">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <TrendingUp className="h-5 w-5 text-green-500 mr-2" />
                        Example Use Cases
                      </h4>
                      <div className="space-y-3">
                        {feature.examples.map((example, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <ChevronRight className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-700">{example}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          ))}
        </div>
      </section>

      {/* Chart Types */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Visualization Options
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Rich chart types and visualization options for every data story
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {chartTypes.map((chart, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="text-4xl mb-3">{chart.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{chart.name}</h3>
                <p className="text-sm text-gray-600">{chart.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Report Categories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Pre-built Report Templates
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Start with industry-standard reports and customize to your needs
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {reportTypes.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">{category.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900">{category.category}</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {category.reports.map((report, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{report}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Analytics Capabilities */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Advanced Analytics
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Go beyond basic reporting with intelligent analytics capabilities
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {analyticsCapabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-4">
                    <capability.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{capability.name}</h3>
                    <p className="text-gray-600">{capability.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {capability.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Zap className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Reporting Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how organizations transformed their decision-making with better reporting
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 text-lg leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-3xl mr-3">{testimonial.avatar}</div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.title}</div>
                      <div className="text-sm text-gray-500">{testimonial.company}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{testimonial.metric}</div>
                    <div className="text-xs text-gray-500">Result</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-emerald-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Transform Your Reporting Today
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Start your free trial and discover how powerful reporting and analytics 
              can drive better business decisions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start Reporting Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <BarChart3 className="mr-2 h-5 w-5" />
                Schedule Analytics Demo
              </motion.a>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ No credit card required  ✓ Full reporting access  ✓ 24/7 support included</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ReportingAnalytics; 